import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ResBlockGetList, ResBlockupdate, ResSaveCreateBlock, ResSaveBlockRoomGrid } from "../../../../Services/Service";
// ^ Adjust the relative path above to match where your Service.js actually
//   lives relative to this slice file's folder.

// ─── Thunk 1: Create Block (Save and Go To Room Grid button) ───
// The API returns an array like: [{ Status: 1, Message: "...", BlockID: 1.0 }]
export const saveCreateBlock = createAsyncThunk(
  "reservationBlock/saveCreateBlock",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await ResSaveCreateBlock(postData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.Message ||
          error?.Message ||
          error?.message ||
          "Something went wrong while saving the block.",
      );
    }
  },
);

// ─── Thunk 2: Room Grid Save (Submit button on RoomGrid) ───
// The API returns an array like: [{ Status: 1, Message: "...", GridID: 1 }]
export const saveBlockRoomGrid = createAsyncThunk(
  "reservationBlock/saveBlockRoomGrid",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await ResSaveBlockRoomGrid(postData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.Message ||
          error?.Message ||
          error?.message ||
          "Something went wrong while saving the room grid.",
      );
    }
  },
);

export const fetchReservationBlocks = createAsyncThunk(
  "reservationBlock/fetchReservationBlocks",
  async (postData, { rejectWithValue }) => {
    try {
      return await ResBlockGetList(postData);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.Message ||
          error?.message ||
          "Failed to fetch blocks.",
      );
    }
  },
);

export const updateReservationBlock = createAsyncThunk(
  "reservationBlock/updateReservationBlock",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await ResBlockupdate(postData);
      if (response?.Success === false) {
        return rejectWithValue(response?.Message || "Failed to update block.");
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.Message ||
          error?.message ||
          "Failed to update block.",
      );
    }
  },
);

const initialState = {
  // Create Block state
  loading: false,
  result: null, // last successful response (array from API)
  error: null,

  // Room Grid Save state
  gridLoading: false,
  gridResult: null, // last successful response (array from API)
  gridError: null,

  // Manage Blocks list state
  listLoading: false,
  blocks: [],
  listError: null,
  updateLoading: false,
  updateResult: null,
  updateError: null,
  pagination: {
    pageNumber: 1,
    pageSize: 5,
    totalRecords: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  },
};

const reservationBlockSlice = createSlice({
  name: "reservationBlock",
  initialState,
  reducers: {
    resetCreateBlockState: (state) => {
      state.loading = false;
      state.result = null;
      state.error = null;
    },
    resetBlockRoomGridState: (state) => {
      state.gridLoading = false;
      state.gridResult = null;
      state.gridError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Block
      .addCase(saveCreateBlock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveCreateBlock.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
      })
      .addCase(saveCreateBlock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to save block.";
      })
      // Room Grid Save
      .addCase(saveBlockRoomGrid.pending, (state) => {
        state.gridLoading = true;
        state.gridError = null;
      })
      .addCase(saveBlockRoomGrid.fulfilled, (state, action) => {
        state.gridLoading = false;
        state.gridResult = action.payload;
      })
      .addCase(saveBlockRoomGrid.rejected, (state, action) => {
        state.gridLoading = false;
        state.gridError = action.payload || "Failed to save room grid.";
      })
      // Manage Blocks list
      .addCase(fetchReservationBlocks.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchReservationBlocks.fulfilled, (state, action) => {
        const response = action.payload || {};
        state.listLoading = false;
        state.blocks = Array.isArray(response.Data) ? response.Data : [];
        state.pagination = {
          pageNumber: response.PageNumber || 1,
          pageSize: response.PageSize || 5,
          totalRecords: response.TotalRecords || 0,
          totalPages: response.TotalPages || 0,
          hasPreviousPage: Boolean(response.HasPreviousPage),
          hasNextPage: Boolean(response.HasNextPage),
        };
      })
      .addCase(fetchReservationBlocks.rejected, (state, action) => {
        state.listLoading = false;
        state.blocks = [];
        state.listError = action.payload || "Failed to fetch blocks.";
      })
      .addCase(updateReservationBlock.pending, (state) => {
        state.updateLoading = true;
        state.updateResult = null;
        state.updateError = null;
      })
      .addCase(updateReservationBlock.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateResult = action.payload;
      })
      .addCase(updateReservationBlock.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || "Failed to update block.";
      });
  },
});

export const { resetCreateBlockState, resetBlockRoomGridState } = reservationBlockSlice.actions;
export default reservationBlockSlice.reducer;
